import React, { useRef } from "react";
import PropTypes from "prop-types";

import BottomSheet from "./BottomSheet";

/**
 * "Upload from" — what Upload document's "Browse files" opens on a phone
 * (Step 80, `MOBILE.uploadSourceSheet`).
 *
 * ONE HIDDEN INPUT PER ROW, because a file input's attributes are the only way
 * a page can steer the platform's picker, and each source wants different ones:
 *
 *   · Camera — `capture` sends a phone straight to the camera app. Desktop
 *     browsers ignore it and open a picker for images.
 *   · Photos — an image-only `accept` opens Android's gallery / Photo Picker.
 *     An iPhone shows its own small menu first (Photo Library / Take Photo /
 *     Choose File); no attribute skips it.
 *   · Files — straight to the document picker (Files, Downloads, Drive), for
 *     a PDF. PDF ONLY, ON PURPOSE (user, 2026-10-05, Step 83): once `accept`
 *     allows an image, the platform puts its own camera / photos / files
 *     choice in front of the picker — Android Chrome always, iOS too — and
 *     that is the Camera and Photos rows over again. With no image type,
 *     Android opens the document picker directly. Images come from Camera or
 *     Photos (an iPhone's Photos menu still has "Choose File" for one kept in
 *     Files). An iPhone may yet show its menu here: WebKit bug 234566, open,
 *     puts it in front of any `accept`, and no attribute skips it.
 *
 * `image/jpeg,image/png`, not `image/*`: iOS converts a HEIC photo to JPEG
 * when `accept` does not list HEIC. Anything else that gets through meets the
 * modal's own rejectReason, which says so in words.
 *
 * THE INPUTS LIVE OUTSIDE THE SHEET. The sheet closes on the tap that opens a
 * picker, and the picker answers later — so the input that hears the answer
 * must not be inside the closed layer's `visibility: hidden`, and must stay
 * mounted. `up-file-input` is the modal's own `display: none` for its input,
 * the one already proven on devices.
 *
 * This decides nothing about the file: it hands it to `onFile`, which is the
 * modal's own takeFile — the same function its own input calls.
 */

const IconCamera = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="12" cy="12" r="3.2" />
    <path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" />
  </svg>
);

const IconPhotos = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
  </svg>
);

const IconFiles = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
  </svg>
);

const SOURCES = [
  {
    key: "camera",
    label: "Camera",
    sub: "Take a photo of the document",
    accept: "image/jpeg,image/png",
    capture: "environment",
    Icon: IconCamera,
  },
  {
    key: "photos",
    label: "Photos",
    sub: "JPG or PNG from your gallery",
    accept: "image/jpeg,image/png",
    Icon: IconPhotos,
  },
  {
    key: "files",
    label: "Files",
    sub: "PDF from your files",
    accept: "application/pdf,.pdf",
    Icon: IconFiles,
  },
];

const MobileFileSourceSheet = ({ open, onClose, onFile }) => {
  const inputs = useRef({});

  /* The picker opens from INSIDE the tap — iOS opens one only from a user
     gesture — and the sheet closes in the same handler. */
  const pick = (key) => {
    const input = inputs.current[key];
    if (input) input.click();
    onClose();
  };

  return (
    <>
      {SOURCES.map((s) => (
        <input
          key={s.key}
          ref={(el) => {
            inputs.current[s.key] = el;
          }}
          type="file"
          className="up-file-input"
          data-source={s.key}
          accept={s.accept}
          capture={s.capture}
          onChange={(e) => {
            const f = e.target.files && e.target.files[0];
            /* Emptied straight away, so the same file picked again after
               Remove still fires `change` — the modal's clearFile empties its
               own input only. The File object outlives the reset. */
            e.target.value = "";
            if (f) onFile(f);
          }}
        />
      ))}

      <BottomSheet open={open} title="Upload from" onClose={onClose} overDialog>
        <div className="zym-src-list">
          {SOURCES.map(({ key, label, sub, Icon }) => (
            <button
              key={key}
              type="button"
              className="zym-src-row"
              data-source={key}
              onClick={() => pick(key)}
            >
              <span className="zym-src-icon" aria-hidden="true">
                <Icon />
              </span>
              <span className="zym-src-text">
                <span className="zym-src-label">{label}</span>
                <span className="zym-src-sub">{sub}</span>
              </span>
            </button>
          ))}
        </div>
      </BottomSheet>
    </>
  );
};

MobileFileSourceSheet.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  /** Receives the picked File — the modal's own takeFile. */
  onFile: PropTypes.func.isRequired,
};

export default MobileFileSourceSheet;
